import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import {
  PropertyData,
  PropertyLabel,
} from "components/EmailEventDetails/styled";
import useClipboard from "utils/useClipboard";
import { useFetch } from "utils/api/useFetch";
import { EventItem, MalwareFile } from "@dashboard-v3/api";
import MalwareDetails from "./MalwareDetails";
import MalwareFiles from "./MalwareFiles";

const emptyValue = "—";

type Props = {
  item: EventItem;
  onClose: () => void;
};

export default function EventDetails({ item, onClose }: Props) {
  const { t } = useTranslation("userActivity");
  const classes = useStyles();
  const { clientIp, createdAt, event, id, sessionId, user, userAgent } = item;
  // Events like the login itself happen outside a session.
  const hasSession = Boolean(sessionId) && sessionId !== "unauthenticated";
  const malwareEvent = event.type === "malware_released" ? event : null;
  const [activeTab, setActiveTab] = useState(0);
  const [filesRequested, setFilesRequested] = useState(false);
  const filesUrl =
    malwareEvent && filesRequested
      ? `/mxhero-shares/malware/${malwareEvent.data.checksum}/files`
      : undefined;
  const files = useFetch<MalwareFile[]>(filesUrl, { inmutable: true });

  const filesTabLabel = files.data
    ? `${t("details.tabs.files")} (${files.data.length})`
    : t("details.tabs.files");
  const showFiles = malwareEvent && activeTab === 1;
  const maxWidth = malwareEvent ? "md" : "sm";

  function onTabChange(_: unknown, tab: number) {
    setActiveTab(tab);
    if (tab === 1) setFilesRequested(true);
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth={maxWidth}>
      <DialogTitle
        disableTypography
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">{t("details.title")}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {malwareEvent && (
        <Tabs
          value={activeTab}
          onChange={onTabChange}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabs}
        >
          <Tab label={t("details.tabs.threat")} data-testid="tab-threat" />
          <Tab label={filesTabLabel} data-testid="tab-files" />
        </Tabs>
      )}
      <DialogContent dividers>
        {showFiles && (
          <MalwareFiles
            files={files.data}
            isLoading={files.isLoading}
            error={files.error}
          />
        )}
        {!showFiles && (
          <>
            <Grid container spacing={3}>
              <Property label={t("user")} xs={7}>
                {user}
              </Property>
              <Property label={t("action")} xs={5}>
                {t(`events.${item.event.type}`)}
              </Property>
              <Property label={t("date")} xs={7}>
                {new Date(createdAt).toLocaleString()}
              </Property>
              <Property label={t("clientIp")} xs={5}>
                {clientIp}
              </Property>
              <Property label={t("details.userAgent")} xs={12}>
                {userAgent}
              </Property>
              <Property label={t("details.sessionId")} xs={7}>
                {hasSession && <CopyableId value={sessionId} />}
              </Property>
              <Property label={t("details.eventId")} xs={5}>
                <CopyableId value={id} />
              </Property>
            </Grid>
            {malwareEvent && <MalwareDetails event={malwareEvent} />}
          </>
        )}
      </DialogContent>
      {malwareEvent && (
        <DialogActions>
          <Button
            size="small"
            component="a"
            href="/malware"
            target="_blank"
            rel="noopener"
            variant="contained"
            color="primary"
            disableElevation
            endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
            data-testid="open-malware-list-btn"
          >
            {t("details.openMalwareList")}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingLeft: 16,
  },
}));

type PropertyProps = {
  label: string;
  xs: 5 | 7 | 12;
  children: React.ReactNode;
};

function Property({ label, xs, children }: PropertyProps) {
  return (
    <Grid item xs={xs}>
      <PropertyLabel>{label}</PropertyLabel>
      <PropertyData color="textPrimary">{children || emptyValue}</PropertyData>
    </Grid>
  );
}

function CopyableId({ value }: { value?: string }) {
  const { t } = useTranslation("userActivity");
  const copyToClipboard = useClipboard();

  if (!value) return null;

  return (
    <Box component="span" display="flex" alignItems="center" gridGap="4px">
      <Tooltip title={value}>
        <span>{shorten(value)}</span>
      </Tooltip>
      <Tooltip title={t("details.copy")}>
        <IconButton
          size="small"
          data-testid="copy-btn"
          onClick={() => copyToClipboard(value)}
        >
          <FileCopyOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/**
 * Session ids are SHA-256 hashes and event ids are Couchbase document keys,
 * both too long for the dialog. Keeping head and tail makes them recognizable,
 * the tooltip and the copy button give the complete value.
 */
function shorten(value: string) {
  const lastSegment = value.split(":").pop();
  if (lastSegment.length <= 20) return lastSegment;
  return `${lastSegment.slice(0, 8)}…${lastSegment.slice(-4)}`;
}
